import React, { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Switch,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import ListAltIcon from '@mui/icons-material/ListAlt';
import StorageIcon from '@mui/icons-material/Storage';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CloseIcon from '@mui/icons-material/Close';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SecurityIcon from '@mui/icons-material/Security';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { signOut } from 'firebase/auth';
import { auth } from '../../../api/firebase/config';
import { createCoupon, updateCoupon } from '../../../api/firebase/function/couponFunction';
import { recordClientAuditEventWithTimeout } from '../../../api/firebase/function/security/clientAuditEventFunction';
import {
  auditAction,
  auditResourceType,
  LOGOUT_AUDIT_LOG_TIMEOUT_MS,
  zIndexLayer,
} from '../../../constants/enum';
import { referralMessages, couponMessages } from '../../../constants/messages';
import { AuthContext } from '../../../contexts/auth';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import { isOperatorStaff, isNotCompanyMember } from '../../../utils/permission';
import { clearPatternListCache } from '../../../hooks/usePatternList';
import { useApiMenuVisibility } from '../../../hooks/useApiMenuVisibility';
import { useSecurityAddonMenuVisibility } from '../../../hooks/useSecurityAddonMenuVisibility';
import { useEmailMenuVisibility } from '../../../hooks/useEmailMenuVisibility';
import { useDetailMode } from '../../../hooks/useDetailMode';
import {
  gray30,
  gray5,
  gray40,
  gray80,
  primaryButtonBackgroundColor,
} from '../../../constants/colors';
import { globalHeaderHeight, spHeaderHeight, dialogTitle } from '../../../constants/location';
import {
  PROXY_BANNER_HEIGHT,
  GROUP_MANAGEMENT_TITLE,
  ACCOUNT_MANAGEMENT_TITLE,
  STAFF_LIST_TITLE,
  DETAIL_MODE_MENU_LABEL,
} from '../../../constants/labels';
import AccountCircle from '../../assets/Account circle.svg';
import ReferralInviteDialog from '../dialog/ReferralInviteDialog';
import CustomSnackbar from '../form/CustomSnackbar';
import CustomLoading from '../form/CustomLoading';

const GlobalHeader = (): React.ReactElement | null => {
  const { user } = useContext(AuthContext);
  const { isProxyLogin } = useContext(ProxyLoginContext);
  const { isDetailView, toggleDetailMode } = useDetailMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarErrorText, setSnackbarErrorText] = useState('');
  const [snackbarInfoText, setSnackbarInfoText] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponNote, setCouponNote] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [createdCouponId, setCreatedCouponId] = useState('');
  const [createdCouponUrl, setCreatedCouponUrl] = useState('');
  const [savedCouponCode, setSavedCouponCode] = useState('');
  const [savedCouponNote, setSavedCouponNote] = useState('');
  // ログアウト処理中（#3110）: ローディング表示と二重実行の防止に使う
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // ヘッダーが出てるページ、またはサイドバーがないページではグローバルヘッダーを非表示にする
  const hideGlobalHeader =
    (location.pathname.startsWith('/ai-ocr/pattern/') && location.pathname.includes('/result/')) ||
    location.pathname.startsWith('/ai-ocr/free/result/') ||
    // 認証関連ページ（サイドバーなし）
    location.pathname === '/' ||
    location.pathname.startsWith('/auth/') ||
    location.pathname.startsWith('/action') ||
    location.pathname.startsWith('/staff/register') ||
    location.pathname.startsWith('/staff/invite/cancel') ||
    location.pathname === '*';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string): void => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogout = async (): Promise<void> => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    handleMenuClose();
    try {
      // 監査ログ（#2985）: ログアウト。signOut 後は未認証になるため先に記録する。
      // ただし callable のコールドスタート等で待たされ続けないよう上限時間を設ける（#3110）
      await recordClientAuditEventWithTimeout(
        {
          action: auditAction.logout,
          resource_type: auditResourceType.session,
        },
        LOGOUT_AUDIT_LOG_TIMEOUT_MS,
      );
      // パターンリストのキャッシュをクリア
      clearPatternListCache();
      await signOut(auth);
      navigate('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[GlobalHeader] ログアウトエラー:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleInvite = (): void => {
    handleMenuClose();
    setInviteDialogOpen(true);
  };

  const generateCouponCode = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // 紛らわしい文字を除外
    const random = Array.from(
      { length: 6 },
      () => chars[Math.floor(Math.random() * chars.length)],
    ).join('');
    return `GD-${random}`;
  };

  const handleOpenCouponDialog = (): void => {
    handleMenuClose();
    // 作成済みのクーポンがなければ新しいコードを生成
    if (!createdCouponId) {
      setCouponCode(generateCouponCode());
      setCouponNote('');
    }
    setCouponDialogOpen(true);
  };

  const handleCloseCouponDialog = (): void => {
    setCouponDialogOpen(false);
    // ダイアログを閉じたらリセット（次回は新規クーポン作成）
    setCreatedCouponId('');
    setCreatedCouponUrl('');
    setSavedCouponCode('');
    setSavedCouponNote('');
    setCouponCode('');
    setCouponNote('');
  };

  const isCouponChanged = createdCouponId
    ? couponCode.trim() !== savedCouponCode || couponNote.trim() !== savedCouponNote
    : false;

  const handleCouponAction = async (): Promise<void> => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      if (!createdCouponId) {
        // 未作成：新規作成
        const response = await createCoupon(couponCode.trim(), couponNote.trim() || undefined);
        if (response.success) {
          setCreatedCouponId(response.data.coupon_id);
          setCreatedCouponUrl(response.data.coupon_url);
          setSavedCouponCode(couponCode.trim());
          setSavedCouponNote(couponNote.trim());
          await navigator.clipboard.writeText(response.data.coupon_url);
          setSnackbarInfoText(couponMessages.created(response.data.code));
          setSnackbarErrorText('');
          setSnackbarOpen(true);
        }
      } else if (isCouponChanged) {
        // 作成済み＋変更あり：更新
        const response = await updateCoupon(
          createdCouponId,
          couponCode.trim(),
          couponNote.trim() || undefined,
        );
        if (response.success) {
          setCreatedCouponUrl(response.data.coupon_url);
          setSavedCouponCode(couponCode.trim());
          setSavedCouponNote(couponNote.trim());
          await navigator.clipboard.writeText(response.data.coupon_url);
          setSnackbarInfoText(couponMessages.updated(response.data.code));
          setSnackbarErrorText('');
          setSnackbarOpen(true);
        }
      } else {
        // 作成済み＋変更なし：コピーのみ
        await navigator.clipboard.writeText(createdCouponUrl);
        setSnackbarInfoText(couponMessages.created(savedCouponCode));
        setSnackbarErrorText('');
        setSnackbarOpen(true);
      }
    } catch (error: unknown) {
      const fallback = createdCouponId ? couponMessages.updateFailed : couponMessages.createFailed;
      const message =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : fallback;
      setSnackbarErrorText(message);
      setSnackbarInfoText('');
      setSnackbarOpen(true);
    } finally {
      setCouponLoading(false);
    }
  };

  const getCouponButtonLabel = (): string => {
    if (!createdCouponId) return couponMessages.createAndCopyButton;
    if (isCouponChanged) return couponMessages.updateAndCopyButton;
    return couponMessages.createAndCopyButton;
  };

  const isOperator = user && isOperatorStaff(user.role);
  // 運営向けメニュー（#3203）: 代理ログイン中は代理先企業の担当者としての操作に絞り、
  // 運営機能は代理を終了してから使う。運営自身のメニューは ProxyModeBanner 側にある。
  // 企業一覧・全読取パターンは PROXY_RESTRICTED_PATHS で遷移が塞がれており
  // 「押せるが行けない」状態だったため、導線ごと消す。
  const showOperatorMenu = isOperator && !isProxyLogin;
  // 詳細モード（#3069）: 運営スタッフが通常ログイン中に内部情報（コスト・削除済み等）の
  // 表示をON/OFFできるようにする。代理ログイン中は ProxyModeBanner 側のトグルを使うため出さない。
  const showDetailModeToggle = showOperatorMenu;
  const showApiKeyMenu = useApiMenuVisibility();
  const showSecurityMenu = useSecurityAddonMenuVisibility();
  const showEmailMenu = useEmailMenuVisibility();

  if (hideGlobalHeader) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          height: globalHeaderHeight,
          backgroundColor: gray5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 32px',
          marginTop: isProxyLogin ? `${PROXY_BANNER_HEIGHT}px` : 0,
          '@media screen and (max-width:960px)': {
            marginTop: 0,
            position: 'fixed',
            top: spHeaderHeight,
            left: 0,
            right: 0,
            zIndex: zIndexLayer.globalHeader,
            height: spHeaderHeight,
            padding: '0 16px',
          },
        }}
      >
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            padding: 0,
          }}
          aria-label="アカウントメニュー"
        >
          <Avatar
            src={user?.icon || AccountCircle}
            sx={{
              width: 30,
              height: 30,
              backgroundColor: 'transparent',
              border: `1px solid ${gray30}`,
            }}
          />
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: '216px',
            marginTop: '8px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '2px 2px 4px 1px rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <MenuItem onClick={(): void => handleNavigate('/company')}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{ACCOUNT_MANAGEMENT_TITLE}</ListItemText>
        </MenuItem>

        {showOperatorMenu && (
          <MenuItem onClick={(): void => handleNavigate('/admin/company')}>
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>企業一覧</ListItemText>
          </MenuItem>
        )}

        {showOperatorMenu && (
          <MenuItem onClick={(): void => handleNavigate('/admin/patterns')}>
            <ListItemIcon>
              <ListAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>全ての読取パターン</ListItemText>
          </MenuItem>
        )}

        {showOperatorMenu && (
          <MenuItem onClick={(): void => handleNavigate('/admin/masters')}>
            <ListItemIcon>
              <StorageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>全てのマスタ</ListItemText>
          </MenuItem>
        )}

        {showOperatorMenu && (
          <MenuItem onClick={handleOpenCouponDialog}>
            <ListItemIcon>
              <ConfirmationNumberIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{couponMessages.menuLabel}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={(): void => handleNavigate('/staff')}>
          <ListItemIcon>
            <PeopleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{STAFF_LIST_TITLE}</ListItemText>
        </MenuItem>

        {/* グループ管理（#2804）: 企業管理者・運営のみ表示 */}
        {user && isNotCompanyMember(user.role) && (
          <MenuItem onClick={(): void => handleNavigate('/group')}>
            <ListItemIcon>
              <GroupsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{GROUP_MANAGEMENT_TITLE}</ListItemText>
          </MenuItem>
        )}

        {showApiKeyMenu && (
          <MenuItem onClick={(): void => handleNavigate('/company-settings/api-keys')}>
            <ListItemIcon>
              <VpnKeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>APIキー管理</ListItemText>
          </MenuItem>
        )}

        {showSecurityMenu && (
          <MenuItem onClick={(): void => handleNavigate('/company-settings/security')}>
            <ListItemIcon>
              <SecurityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>セキュリティ設定</ListItemText>
          </MenuItem>
        )}

        {/* アドレス管理（#2965/#3042）: 専用アドレスの一覧・管理へ。受信トレイ本体はサイドバーに配置 */}
        {showEmailMenu && (
          <MenuItem onClick={(): void => handleNavigate('/company-settings/email-inboxes')}>
            <ListItemIcon>
              <AlternateEmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>受信アドレス管理</ListItemText>
          </MenuItem>
        )}

        {!isOperator && (
          <MenuItem onClick={handleInvite}>
            <ListItemIcon>
              <PersonAddAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{referralMessages.menuLabel}</ListItemText>
          </MenuItem>
        )}

        {/* 詳細モード切替（#3069）: 運営スタッフの通常ログイン時のみ。
            切替後の表示をその場で確認できるようメニューは閉じない。 */}
        {showDetailModeToggle && (
          <>
            <Divider />
            <MenuItem onClick={toggleDetailMode}>
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{DETAIL_MODE_MENU_LABEL}</ListItemText>
              <Switch checked={isDetailView} size="small" />
            </MenuItem>
            <Divider />
          </>
        )}

        <MenuItem onClick={handleLogout} disabled={isLoggingOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>ログアウト</ListItemText>
        </MenuItem>
      </Menu>

      {/* ログアウト処理中のローディング（#3110） */}
      <CustomLoading loading={isLoggingOut} />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarErrorText}
        infoText={snackbarInfoText}
      />

      <ReferralInviteDialog
        open={inviteDialogOpen}
        onClose={(): void => setInviteDialogOpen(false)}
      />

      <Dialog
        open={couponDialogOpen}
        onClose={handleCloseCouponDialog}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            border: `1px solid ${gray40}`,
            boxSizing: 'border-box',
            margin: '16px',
            padding: '40px',
            '@media screen and (max-width: 960px)': {
              padding: '32px 16px',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            ...dialogTitle,
            padding: 0,
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {couponMessages.dialogTitle}
          <IconButton onClick={handleCloseCouponDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            padding: 0,
            overflow: 'visible',
          }}
        >
          <TextField
            label={couponMessages.codeLabel}
            value={couponCode}
            onChange={(e): void => setCouponCode(e.target.value.toUpperCase())}
            fullWidth
            helperText={couponMessages.codeHelperText}
          />
          <TextField
            label={couponMessages.noteLabel}
            placeholder={couponMessages.notePlaceholder}
            value={couponNote}
            onChange={(e): void => setCouponNote(e.target.value)}
            fullWidth
          />
          <Box sx={{ fontSize: '13px', color: gray80 }}>{couponMessages.rewardDescription}</Box>
        </DialogContent>
        <DialogActions sx={{ padding: 0, marginTop: '24px' }}>
          <Button
            onClick={handleCouponAction}
            disabled={!couponCode.trim() || couponLoading}
            sx={{
              ...primaryButtonBackgroundColor,
              height: '44px',
              width: '100%',
              fontSize: '14px',
              fontWeight: 700,
            }}
            variant="contained"
          >
            {couponLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              getCouponButtonLabel()
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GlobalHeader;
